import React, { Component } from 'react';
import FormicaInput from '../../custom/FormicaInput';
import RoleHelper from '../../../RoleHelper';
import Icon from '../../../icons/Icon';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import FormicaCalendarComponent from '../../custom/FormicaCalendarComponent';
import TrackerFilterJsonModel from '../../../domain/tracker/filter/TrackerFilterJsonModel';
import TrackerFilterOperator from '../../../domain/tracker/filter/TrackerFilterOperator';
import DataType from '../../../constant/DataType';
import TrackerTypeUtil from '../../../util/TrackerTypeUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import { CalendarChangeParams } from 'primereact/calendar';

interface FilterFieldOption {
  label: string;
  value: string;
  dataType: DataType;
}

interface Props {
  fieldOptions: FilterFieldOption[];
  filters: TrackerFilterJsonModel[];
  newFilter: TrackerFilterJsonModel;
  selectedFieldDataType?: DataType;
  onAddFilter: () => void;
  onUpdateFilter: (index: number, key: keyof TrackerFilterJsonModel, value: any) => void;
  onUpdateNewFilter: (key: keyof TrackerFilterJsonModel, value: any) => void;
  onRemoveFilter: (index: number) => void;
  onSelectedFieldChange?: (field: string, dataType: DataType) => void;
}

class TrackerFilterComponent extends Component<Props> {
  private getDataTypeForField = (field: string): DataType | undefined => {
    const { fieldOptions } = this.props;
    const option = fieldOptions.find((opt) => opt.value === field);
    return option?.dataType;
  };

  private getBooleanOptions = (): FormicaSelectBoxItem[] => {
    return [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ];
  };

  private renderValueInput = (value: string, dataType: DataType | undefined, onChange: (value: any) => void, disabled: boolean) => {
    switch (dataType) {
      case DataType.DATE: {
        let dateValue: Date | null = null;
        if (value) {
          if (!isNaN(Number(value)) && value.trim() !== '') {
            const timestamp = Number(value);
            dateValue = new Date(timestamp);
            if (isNaN(dateValue.getTime())) {
              dateValue = null;
            }
          } else {
            const parsedDate = new Date(value);
            if (!isNaN(parsedDate.getTime())) {
              dateValue = parsedDate;
            }
          }
        }
        return (
          <FormicaCalendarComponent
            value={dateValue}
            onChange={(e: CalendarChangeParams) => {
              const selectedDate = e.value as Date | Date[] | null;
              if (selectedDate && !Array.isArray(selectedDate)) {
                onChange(selectedDate.getTime().toString());
              } else {
                onChange('');
              }
            }}
            dateFormat='dd/mm/yy'
            showTime
            hourFormat='24'
            disabled={disabled}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.value', defaultMessage: 'Value' })}
          />
        );
      }
      case DataType.BOOLEAN:
        return (
          <FormicaSearchSelectBox
            label={'Value'}
            value={value}
            options={this.getBooleanOptions()}
            onChange={(e: FormicaSelectBoxItem) => {
              onChange(e.value);
            }}
            rounded
            disabled={disabled}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.value', defaultMessage: 'Value' })}
          />
        );
      default:
        return (
          <FormicaInput
            name={'Value'}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              onChange(e.currentTarget.value);
            }}
            type={'text'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.value', defaultMessage: 'Value' })}
            disabled={disabled}
            value={value}
          />
        );
    }
  };

  public render() {
    const { fieldOptions, filters, newFilter, selectedFieldDataType, onAddFilter, onUpdateFilter, onUpdateNewFilter, onRemoveFilter, onSelectedFieldChange } =
      this.props;

    return (
      <div className={'border-b py-5'}>
        <div className={'flex flex-col gap-y-1'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.filter', defaultMessage: 'Filter' })} />
          <div className={'flex items-center justify-center gap-x-1 py-1'}>
            <FormicaSearchSelectBox
              label={'Field'}
              value={newFilter.field}
              options={fieldOptions.map((option) => {
                return { label: option.label, value: option.value };
              })}
              onChange={(e: FormicaSelectBoxItem) => {
                const selectedOption = fieldOptions.find((opt) => opt.value === e.value);
                if (selectedOption) {
                  if (onSelectedFieldChange) {
                    onSelectedFieldChange(selectedOption.value, selectedOption.dataType);
                  }
                  onUpdateNewFilter('field', selectedOption.value);
                }
              }}
              rounded
              disabled={!RoleHelper.canEdit('Tracker')}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.field', defaultMessage: 'Field' })}
            />
            <FormicaSearchSelectBox
              label={'Operator'}
              value={newFilter.operator}
              options={TrackerTypeUtil.getFilterOptions(selectedFieldDataType || DataType.STRING)}
              onChange={(e: FormicaSelectBoxItem) => {
                onUpdateNewFilter('operator', e.value as TrackerFilterOperator);
              }}
              rounded={true}
              disabled={!RoleHelper.canEdit('Tracker')}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.operator', defaultMessage: 'Operator' })}
            />
            {this.renderValueInput(newFilter.value || '', selectedFieldDataType, (value) => onUpdateNewFilter('value', value), !RoleHelper.canEdit('Tracker'))}
            <div className={'min-w-[10px] min-h-[10px] cursor-pointer'} onClick={onAddFilter}>
              <Icon icon={'add'} size={24} color={'#458EF7'} />
            </div>
          </div>
        </div>
        <div>
          {filters.map((filter: TrackerFilterJsonModel, index: number) => {
            const filterDataType = this.getDataTypeForField(filter.field);
            return (
              <div key={index} className={'flex items-center gap-x-1 py-1'}>
                <FormicaInput
                  name={'Field'}
                  type={'text'}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.field', defaultMessage: 'Field' })}
                  disabled={false}
                  value={filter.field}
                />
                <FormicaInput
                  name={'Operator'}
                  type={'text'}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.operator', defaultMessage: 'Operator' })}
                  disabled={false}
                  value={filter.operator}
                />
                {this.renderValueInput(filter.value || '', filterDataType, (value) => onUpdateFilter(index, 'value', value), !RoleHelper.canEdit('Tracker'))}
                <div className={'min-w-[10px] min-h-[10px] cursor-pointer'} onClick={() => onRemoveFilter(index)}>
                  <Icon icon={'delete'} size={20} color={'#458EF7'} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default TrackerFilterComponent;
